/**
 * Heuristic auto-arrange layouts. All math in canvas-logical coordinates (0..size).
 * Each function returns: [{ id, x, y, width, height, rotation }]
 * Layers passed in are: [{ id, naturalWidth, naturalHeight }]
 */
const rand = (min, max) => Math.random() * (max - min) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ---- Grid (clean, magazine-style) ----
function gridLayout(layers, size) {
  const n = layers.length;
  if (n === 0) return [];
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  const pad = size * 0.04;
  const gap = size * 0.02;
  const cellW = (size - pad * 2 - gap * (cols - 1)) / cols;
  const cellH = (size - pad * 2 - gap * (rows - 1)) / rows;

  return layers.map((l, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const ar = l.naturalWidth / Math.max(1, l.naturalHeight);
    let w = cellW, h = cellH;
    // fit aspect
    if (cellW / cellH > ar) w = cellH * ar; else h = cellW / ar;
    const cx = pad + col * (cellW + gap) + cellW / 2;
    const cy = pad + row * (cellH + gap) + cellH / 2;
    return {
      id: l.id,
      x: cx - w / 2,
      y: cy - h / 2,
      width: w,
      height: h,
      rotation: 0,
    };
  });
}

// ---- Scatter (creative, with rotations & overlap) ----
function scatterLayout(layers, size) {
  const target = size * 0.34; // average diagonal target
  return layers.map((l) => {
    const ar = l.naturalWidth / Math.max(1, l.naturalHeight);
    const scale = rand(0.7, 1.2);
    let w, h;
    if (ar > 1) { w = target * scale; h = w / ar; }
    else { h = target * scale; w = h * ar; }
    const x = rand(size * 0.05, size - w - size * 0.05);
    const y = rand(size * 0.05, size - h - size * 0.05);
    return {
      id: l.id,
      x, y, width: w, height: h,
      rotation: rand(-12, 12),
    };
  });
}

// ---- Polaroid Stack (centered, slight offsets) ----
function polaroidStack(layers, size) {
  const baseW = size * 0.55;
  const cx = size / 2, cy = size / 2;
  return layers.map((l, i) => {
    const ar = l.naturalWidth / Math.max(1, l.naturalHeight);
    let w = baseW * rand(0.85, 1.05);
    let h = w / ar;
    const dx = rand(-size * 0.12, size * 0.12) + (i - layers.length / 2) * 12;
    const dy = rand(-size * 0.10, size * 0.10) + (i - layers.length / 2) * 8;
    return {
      id: l.id,
      x: cx + dx - w / 2,
      y: cy + dy - h / 2,
      width: w,
      height: h,
      rotation: rand(-10, 10),
    };
  });
}

// ---- Gallery Wall (rows of varied heights, clean) ----
function galleryWall(layers, size) {
  const items = layers.map((l) => ({
    ...l,
    ar: l.naturalWidth / Math.max(1, l.naturalHeight),
  }));
  const pad = size * 0.04;
  const gap = size * 0.018;
  const usable = size - pad * 2;

  // Distribute into rows aiming for similar row heights
  const targetRows = Math.max(1, Math.round(Math.sqrt(items.length)));
  const perRow = Math.ceil(items.length / targetRows);
  const rowH = (usable - gap * (targetRows - 1)) / targetRows;

  const out = [];
  for (let r = 0; r < targetRows; r++) {
    const slice = items.slice(r * perRow, (r + 1) * perRow);
    if (!slice.length) continue;
    const totalAr = slice.reduce((s, it) => s + it.ar, 0);
    let rowW = usable;
    let h = Math.min(rowH, rowW / totalAr);
    let usedW = slice.reduce((s, it) => s + it.ar * h, 0) + gap * (slice.length - 1);
    // center
    let xCursor = pad + (usable - usedW) / 2;
    const y = pad + r * (rowH + gap) + (rowH - h) / 2;
    for (const it of slice) {
      const w = it.ar * h;
      out.push({
        id: it.id,
        x: xCursor, y, width: w, height: h, rotation: 0,
      });
      xCursor += w + gap;
    }
  }
  return out;
}

// ---- Diagonal Sweep (dynamic, energetic) ----
function diagonalSweep(layers, size) {
  const n = layers.length;
  const margin = size * 0.06;
  const target = (size * 0.45) / Math.max(1, Math.sqrt(n));
  return layers.map((l, i) => {
    const ar = l.naturalWidth / Math.max(1, l.naturalHeight);
    const t = i / Math.max(1, n - 1); // 0..1
    let w = target * rand(1.6, 2.4);
    let h = w / ar;
    if (h > size * 0.6) { h = size * 0.6; w = h * ar; }
    const cx = margin + (size - margin * 2) * t + rand(-30, 30);
    const cy = margin + (size - margin * 2) * t + rand(-30, 30);
    return {
      id: l.id,
      x: Math.max(margin, Math.min(size - margin - w, cx - w / 2)),
      y: Math.max(margin, Math.min(size - margin - h, cy - h / 2)),
      width: w, height: h,
      rotation: rand(-8, 8),
    };
  });
}

const STYLES = [
  { name: 'Magazine Grid', fn: gridLayout },
  { name: 'Scatter', fn: scatterLayout },
  { name: 'Polaroid Stack', fn: polaroidStack },
  { name: 'Gallery Wall', fn: galleryWall },
  { name: 'Diagonal Sweep', fn: diagonalSweep },
];

/**
 * Run a smart heuristic auto-arrange. Pass current layers (with naturalWidth/Height)
 * and canvas size. Returns { style, layouts } where layouts is an array of
 * { id, x, y, width, height, rotation }.
 */
export function smartAutoArrange(layers, size, lastStyle = null) {
  if (!layers.length) return { style: 'empty', layouts: [] };
  // Avoid repeating same style if user clicks again
  let pool = STYLES;
  if (lastStyle) pool = STYLES.filter((s) => s.name !== lastStyle);
  const choice = pick(pool);
  return { style: choice.name, layouts: choice.fn(layers, size) };
}

export const AVAILABLE_STYLES = STYLES.map((s) => s.name);
